import {
	afterNextRender,
	Component,
	computed,
	effect,
	inject,
	isDevMode,
	signal
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, Subscription } from 'rxjs';

import { releaseChunkReload } from '@demeter/lib/utils/chunk-load-error';
import { AuthService } from '@demeter/modules/auth';
import { EXPLICIT_SIGNOUT_KEY } from '@demeter/modules/auth/auth.keys';
import { NavBarComponent } from '@demeter/modules/nav-bar';
import {
	PresentationModeService,
	SettingsDrawerComponent,
	UserSettingsService
} from '@demeter/modules/settings';

import { LoadFailureComponent } from './load-failure/load-failure.component';

@Component({
	selector: 'df-root',
	imports: [RouterOutlet, NavBarComponent, LoadFailureComponent, SettingsDrawerComponent],
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss'
})
export class AppComponent {
	title = 'Demeter Funds';
	isFullscreen = signal(false);

	private readonly authService = inject(AuthService);
	private readonly userSettings = inject(UserSettingsService);
	private readonly presentation = inject(PresentationModeService);

	readonly contentState = computed<'failed' | 'signed-out' | 'routed'>(() => {
		const status = this.authService.status();
		if (status === 'failed') {
			return 'failed';
		}
		if (status === 'unauthenticated' && sessionStorage.getItem(EXPLICIT_SIGNOUT_KEY)) {
			return 'signed-out';
		}
		return 'routed';
	});

	private subs: Subscription[] = [];

	constructor(
		private router: Router,
		private route: ActivatedRoute
	) {
		afterNextRender(() => window.__dfBootOk?.());
		effect(() => {
			if (this.authService.status() === 'authenticated') {
				this.userSettings.load();
				this.presentation.load();
			}
		});
	}

	ngOnInit(): void {
		// Store preference before MSAL redirect so it survives the full-page reload
		const initParams = new URLSearchParams(window.location.search);
		if (initParams.get('fullscreen') === 'true' || initParams.get('fullscreen') === '1') {
			sessionStorage.setItem('df-fullscreen', 'true');
		} else {
			sessionStorage.removeItem('df-fullscreen');
		}
		if (sessionStorage.getItem('df-fullscreen') === 'true') {
			this.isFullscreen.set(true);
		}

		this.subs.push(
			// Catch post-auth router.navigateByUrl('/path?fullscreen=true') if it fires
			this.router.events
				.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
				.subscribe(e => {
					releaseChunkReload();
					this._applyFullscreenParam(e.urlAfterRedirects);

					if (isDevMode()) {
						setTimeout(() => {
							const el = document.documentElement;
							if (el.scrollWidth > el.clientWidth) {
								console.warn(
									`[layout] ${e.urlAfterRedirects} widens the document: ` +
										`scrollWidth=${el.scrollWidth} clientWidth=${el.clientWidth}`
								);
							}
						});
					}
				})
		);
	}

	ngOnDestroy(): void {
		this.subs.forEach(s => s.unsubscribe());
		this.subs = [];
	}

	toggleFullscreen(): void {
		this.isFullscreen.update(v => !v);
	}

	private _applyFullscreenParam(url: string): void {
		const params = new URLSearchParams(url.includes('?') ? url.split('?')[1] : url);
		if (params.get('fullscreen') === 'true' || params.get('fullscreen') === '1') {
			this.isFullscreen.set(true);
		}
	}
}
